import { useMemo } from 'react';
import { ArrowDown } from 'lucide-react';
import { AuthorLink } from '@/components/AuthorLink';
import { PersonaColumns } from '@/components/PersonaColumns';
import { TrackGraph } from '@/components/TrackGraph';
import { manifest } from '@/lib/content';
import { personas } from '@/lib/personas';

function useFieldStats() {
  return useMemo(() => {
    const learn = manifest.sections.filter((s) => s.kind === 'learn');
    const chapters = learn.reduce(
      (sum, s) => sum + s.chapters.filter((c) => !c.draft).length,
      0,
    );
    return { tracks: learn.length, chapters, roles: personas.length };
  }, []);
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-sans text-2xl font-semibold tracking-tight text-ink">
        {value}
      </div>
      <div className="mt-0.5 font-sans text-2xs uppercase tracking-[0.12em] text-ink-mute">
        {label}
      </div>
    </div>
  );
}

export default function Home() {
  const stats = useFieldStats();

  return (
    <div className="container-prose">
      {/* Hero. Sized to hold the fold on its own so the first scroll is a
          decision (Start Here), not an accident into the next section. */}
      <section className="flex min-h-[calc(100vh-4rem)] flex-col justify-center py-16">
        <div className="max-w-4xl">
          <div aria-hidden="true" className="h-1 w-14 bg-accent" />
          <h1 className="mt-7 font-sans text-[2.75rem] font-semibold leading-[1.04] tracking-[-0.02em] text-ink sm:text-6xl lg:text-7xl">
            The most useful, opinionated reference for AI evals.
          </h1>
          <p className="mt-7 max-w-2xl font-serif text-lg leading-relaxed text-ink-soft md:text-xl">
            Citation-grounded tutorials, playbooks, and tools for the people
            who ship AI. Built by <AuthorLink /> and contributors.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="/start" className="btn-primary text-[0.9375rem]">
              Start Here
            </a>
            <a href="/learn" className="btn-secondary text-[0.9375rem]">
              Browse the field guide
            </a>
          </div>
        </div>

        <div className="mt-16 flex max-w-2xl items-end gap-10 border-t border-rule pt-6">
          <Stat value={String(stats.tracks)} label="Tracks" />
          <Stat value={`${stats.chapters}`} label="Chapters" />
          <Stat value={String(stats.roles)} label="Role paths" />
          <a
            href="#routes"
            className="ml-auto inline-flex items-center gap-2 self-center font-sans text-sm text-ink-mute transition-colors hover:text-ink"
          >
            Explore the map
            <ArrowDown size={15} strokeWidth={1.75} aria-hidden="true" />
          </a>
        </div>
      </section>

      <section id="routes" className="scroll-mt-20 py-16">
        <h2 className="font-sans text-2xl font-semibold tracking-tight text-ink md:text-3xl">
          Pick the route that fits your role
        </h2>
        <p className="mt-3 max-w-prose font-serif text-md leading-relaxed text-ink-soft">
          Each track is a curriculum, not a sampler. Click any node to jump in.
        </p>
        <div className="mt-9">
          <TrackGraph />
        </div>
      </section>

      <section className="py-16">
        <h2 className="font-sans text-2xl font-semibold tracking-tight text-ink md:text-3xl">
          Go deep into one track
        </h2>
        <p className="mt-3 max-w-prose font-serif text-md leading-relaxed text-ink-soft">
          Nine tracks, equal weight. Find your kind of work, then read in order.
        </p>
        <div className="mt-9">
          <PersonaColumns />
        </div>
      </section>
    </div>
  );
}
